import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreState } from 'src/app/core/store';
import { AuthLogoutAction } from 'src/app/shared/store/actions/auth.actions';
import { GuidEmpty } from '../../../core/utilities/constants';
import { BaseComponent } from '../../../shared/components';
import {
  AllMessageTypes,
  Commodity,
  ContactNumberMessageTypes,
  LoadStatusNotificationsData,
  User,
  UserNotification,
  UserNotificationModel,
  ValidationProblemDetails,
} from '../../../shared/models';
import { contactPhoneRequiredValidator } from '../../validators/user-profile-validators';

@Component({
  selector: 'kbxl-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input() user: User;
  @Input() commodities: Commodity[];
  @Input() loadingCommodities: boolean;
  @Input() loading: boolean;
  @Input() error: ValidationProblemDetails;
  @Input() triggerValidation: Subject<boolean>;
  @Input() loadStatusNotifications: LoadStatusNotificationsData;
  @Input() loadStatusNotificationsErrors: ValidationProblemDetails;
  @Input() savedLoadStatusNotifications: boolean;
  @Output() updated: EventEmitter<User> = new EventEmitter<User>();
  @Output() saveLoadStatusNotifications: EventEmitter<LoadStatusNotificationsData> = new EventEmitter<LoadStatusNotificationsData>();
  messageTypes = [];

  userProfileForm: UntypedFormGroup;
  allowSelectingScac = false;
  submitted = false;
  showManageLoadNotifications: false;

  get contactNumbers() {
    const c = this.userProfileForm.get('contactNumbers') as UntypedFormArray;
    return c.controls;
  }

  get allNotificationsEnabled() {
    return this.userProfileForm.get('notificationEnabled').value;
  }
  constructor(private store: Store<CoreState>, private fb: UntypedFormBuilder, private messageService: MessageService) {
    super();
    this.createForm();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.user && changes.user.currentValue) {
      const currentValue = changes.user.currentValue;
      this.allowSelectingScac =
        !currentValue.carrierScac && currentValue.availableCarrierScacsSelectItems && currentValue.availableCarrierScacsSelectItems.length;

      if (currentValue.defaultCommodity && this.commodities) {
        const commodity = this.commodities.find((x) => x.commodityName === currentValue.defaultCommodity);
        if (commodity) {
          this.user.selectedCommodity = commodity;
        }
      }

      this.submitted = false;
      this.updateForm();
    }

    if (changes && changes.savedLoadStatusNotifications) {
      if (this.savedLoadStatusNotifications) {
        this.showManageLoadNotifications = false;
      }
    }
  }
  ngOnInit(): void {
    this.triggerValidation.pipe(takeUntil(this.destroyed$)).subscribe((x) => {
      if (x) {
        this.submitted = true;
        this.userProfileForm.markAsTouched();
        this.userProfileForm.markAsDirty();
        this.validateAllFormFields(this.userProfileForm);

        if (!this.userProfileForm.valid) {
          this.messageService.add({
            summary: 'Error updating User Profile',
            detail: 'One or more errors occurred when updating the User Profile.  See form for error details',
            severity: 'error',
          });
          return;
        }
      }
    });
  }

  createForm(): void {
    // build message type dropdown
    ContactNumberMessageTypes.forEach((x) => {
      this.messageTypes.push({
        label: x.description,
        value: x.value,
      });
    });

    // create form
    this.userProfileForm = new UntypedFormGroup(
      {
        notificationEnabled: new UntypedFormControl(true),
        chatNotificationsEnabled: new UntypedFormControl(true),
        email: new UntypedFormControl('', [Validators.required, Validators.maxLength(256), Validators.email]),
        carrierScac: new UntypedFormControl(''),
        contactNumbers: this.fb.array([]),
        selectedCommodity: new UntypedFormControl(''),
      },
      { validators: contactPhoneRequiredValidator }
    );
  }

  updateForm(): void {
    const items = this.userProfileForm.get('contactNumbers') as UntypedFormArray;

    this.userProfileForm.patchValue({
      email: this.getEmailNotification(),
      carrierScac: this.user.carrierScac,
      notificationEnabled: this.user.features.isNotificationsEnabled,
      chatNotificationsEnabled: this.user.features.isChatNotificationsEnabled,
      selectedCommodity: this.user.selectedCommodity,
    });

    // we need to clear the array without losing the validations
    const phoneNotifications = this.user.userNotifications.filter(
      (x) => x.messageTypeId !== AllMessageTypes.Email && x.userNotificationId !== GuidEmpty
    );

    for (let index = 0; index < phoneNotifications.length; index++) {
      const element = phoneNotifications[index];

      const exists = items.value.find((x) => x.userNotificationId === element.userNotificationId);
      if (!exists) {
        items.push(this.createContactItem(element));
      }
    }

    if (phoneNotifications.length === 0 && items.value.length === 0) {
      this.addContact();
    }
  }

  removeContact(index: number): void {
    const items = this.userProfileForm.get('contactNumbers') as UntypedFormArray;
    items.removeAt(index);
    if (items.value.length === 0) {
      this.addContact();
    }
  }

  addContact(): void {
    this.submitted = false;
    const items = this.userProfileForm.get('contactNumbers') as UntypedFormArray;
    items.push(this.createContactItem());
  }

  createContactItem(notification?: UserNotification): UntypedFormGroup {
    if (!notification) {
      notification = new UserNotificationModel({
        userNotificationId: GuidEmpty,
        messageTypeId: AllMessageTypes.Phone,
        notificationValue: '',
        additionalValue: '',
        isDefault: false,
        notificationEnabled: this.userProfileForm.value.notificationEnabled,
      });
    } else {
      // check if we need to split the extension out
      if (notification.notificationValue.indexOf('x') > -1) {
        const split = notification.notificationValue.split('x');
        notification.notificationValue = split[0];
        notification.additionalValue = split[1];
      }
    }

    return this.fb.group({
      userNotificationId: notification.userNotificationId,
      messageTypeId: notification.messageTypeId,
      notificationValue: notification.notificationValue,
      additionalValue: notification.additionalValue,
      description: notification.description,
      notificationEnabled: notification.notificationEnabled,
    });
  }

  update() {
    this.updated.emit(this.user);
  }

  logout() {
    this.store.dispatch(new AuthLogoutAction());
  }

  onSubmit(): void {
    this.submitted = true;
    this.validateAllFormFields(this.userProfileForm);

    if (!this.userProfileForm.valid) {
      this.messageService.add({
        summary: 'Error updating User Profile',
        detail: 'One or more errors occurred when updating the User Profile.  See form for error details',
        severity: 'error',
      });
      return;
    }
    this.user = this.deepClone(this.user);
    // pull the contact numbers from the form and save to the user object before emitting output
    const formModel = this.userProfileForm.value;

    const emailNotification = this.user.userNotifications.find((x) => x.messageTypeId === AllMessageTypes.Email);

    if (emailNotification) {
      emailNotification.notificationValue = formModel.email as string;
    }

    const contactNumbers = formModel.contactNumbers as UserNotificationModel[];

    // format the extension to append to the notification value
    contactNumbers.forEach((x) => {
      if (x.messageTypeId === AllMessageTypes.Phone && x.additionalValue && x.additionalValue.length > 0) {
        x.notificationValue += `x${x.additionalValue}`;
        x.additionalValue = null;
      }
    });

    // replace contact numbers and email
    this.user.userNotifications = [];
    this.user.userNotifications.push(...contactNumbers);
    this.user.userNotifications.push(emailNotification);

    this.user.defaultCommodity = null;
    if (formModel.selectedCommodity) {
      this.user.defaultCommodity = formModel.selectedCommodity.commodityName;
    }
    this.user.carrierScac = formModel.carrierScac;

    this.user.features.isNotificationsEnabled = formModel.notificationEnabled;
    this.user.features.isChatNotificationsEnabled = formModel.chatNotificationsEnabled;

    this.update();
  }

  getEmailNotification(): string {
    const notification = this.user.userNotifications.find((x) => x.messageTypeId === AllMessageTypes.Email);

    if (notification) {
      return notification.notificationValue;
    }
    return '';
  }

  decodeProblemDetails() {
    if (!this.error || !this.error.errors) {
      return;
    }

    const groupErrors = this.error.errors['urn:UserProfile'];
    if (groupErrors && Array.isArray(groupErrors)) {
      return groupErrors.join('\n');
    } else if (groupErrors && typeof groupErrors === 'string') {
      return groupErrors;
    }
    return;
  }
  resetCommodity(): void {
    this.userProfileForm.patchValue({
      selectedCommodity: '',
    });
  }

  onSaveLoadStatusNotifications(payload: LoadStatusNotificationsData): void {
    this.saveLoadStatusNotifications.emit(payload);
  }

  /*
   ** TODO when upgrade to angular 8, replace with this.userProfileForm.markAllAsTouched();
   */
  private validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      formGroup.markAsTouched();
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity();
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      } else if (control instanceof UntypedFormArray) {
        control.controls.forEach((x) => {
          if (x instanceof UntypedFormGroup) {
            this.validateAllFormFields(x);
          }
        });
      }
    });
  }
}
